/**
 * Sync Helpers - DRY utilities for syncedSupabase create/update operations
 *
 * These factory functions create custom create/update handlers that:
 * 1. Handle "0 rows updated" edge case (upsert fallback)
 * 2. Handle foreign key errors (auto-fix by clearing the FK)
 * 3. Handle duplicate key conflicts (upsert fallback)
 */

import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';

/**
 * Auth gate observable for syncedSupabase `waitFor`.
 *
 * Defined here (not in auth$.js) to avoid circular dependencies:
 * auth$.js → stores → syncHelpers.js ← would create a cycle if syncHelpers imported auth$.
 *
 * auth$.js sets this to true on SIGNED_IN/INITIAL_SESSION and false on SIGNED_OUT.
 * All syncedSupabase stores reference this via defaultSyncConfig.waitFor,
 * which delays remote sync (get) until auth is ready — but does NOT block
 * local persistence loading (localStorage loads immediately).
 */
export const isAuthed$ = observable(false);

const SYNC_ERROR_TYPES = {
  FOREIGN_KEY: '23503',
  DUPLICATE_KEY: '23505',
};

/**
 * Creates a custom create function for syncedSupabase
 * @param {string} tableName - Supabase table name
 * @param {object} options - Options for error handling
 * @param {function} options.onForeignKeyError - Callback to fix data on foreign key error
 * @param {string} options.onConflict - Column(s) for upsert conflict resolution (default: 'id')
 * @param {boolean} options.useUpsert - Use upsert directly instead of insert-then-fallback (for tables with composite unique constraints)
 */
export const createSyncCreate = (tableName, options = {}) => {
  const { onForeignKeyError, onConflict = 'id', useUpsert = false } = options;

  return async (input) => {
    // Sanitize array fields - Legend-State observables may serialize as objects
    const sanitizeArray = (val) =>
      val === undefined ? undefined : Array.isArray(val) ? [...val] : Array.from(val || []);
    const sanitizedInput = {
      ...input,
      ...(input.hidden_block_indices !== undefined && { hidden_block_indices: sanitizeArray(input.hidden_block_indices) }),
      ...(input.tags !== undefined && { tags: sanitizeArray(input.tags) }),
      ...(input.read_indices !== undefined && { read_indices: sanitizeArray(input.read_indices) }),
    };

    // For tables with composite unique constraints, use upsert directly to avoid 409 errors
    if (useUpsert) {
      const { data, error } = await supabase
        .from(tableName)
        .upsert(sanitizedInput, { onConflict })
        .select()
        .single();

      if (error) {
        console.error(`${tableName} upsert error:`, error);
        throw new Error(error.message);
      }
      return data;
    }

    const { data, error } = await supabase
      .from(tableName)
      .insert(sanitizedInput)
      .select()
      .single();

    if (error) {
      const errorCode = error.code;
      console.error(`${tableName} create error:`, error);

      // 23503 - Foreign key constraint violation
      if (errorCode === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
        console.warn(`Foreign key error for ${tableName}, auto-fixing...`);
        const fixedData = onForeignKeyError(sanitizedInput);
        const { data: retryData, error: retryError } = await supabase
          .from(tableName)
          .insert(fixedData)
          .select()
          .single();
        if (retryError) throw new Error(retryError.message);
        return retryData;
      }

      // 23505 - Duplicate key violation - fall back to upsert
      if (errorCode === SYNC_ERROR_TYPES.DUPLICATE_KEY) {
        console.warn(`Insert found duplicate key for ${tableName} ${sanitizedInput.id}, falling back to upsert on ${onConflict}`);
        const { data: upsertData, error: upsertError } = await supabase
          .from(tableName)
          .upsert(sanitizedInput, { onConflict })
          .select()
          .single();

        if (upsertError) {
          if (upsertError.code === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
            const fixedData = onForeignKeyError(sanitizedInput);
            const { data: retryData, error: retryError } = await supabase
              .from(tableName)
              .upsert(fixedData, { onConflict })
              .select()
              .single();
            if (retryError) throw new Error(retryError.message);
            return retryData;
          }
          throw new Error(upsertError.message);
        }
        return upsertData;
      }

      throw new Error(error.message);
    }

    return data;
  };
};

/**
 * Creates a custom update function for syncedSupabase
 * Handles the "0 rows updated" edge case by falling back to upsert
 *
 * IMPORTANT: Always ensures updated_at is set, especially for delete operations.
 * Without this, incremental sync (changesSince: 'last-sync') won't see the change.
 *
 * @param {string} tableName - Supabase table name
 * @param {object} options - Options for error handling
 * @param {function} options.onForeignKeyError - Callback to fix data on foreign key error
 * @param {string} options.onConflict - Column(s) for upsert conflict resolution (default: 'id')
 * @param {string[]} options.excludeFieldsOnUpdate - Fields to exclude from UPDATE payloads (not INSERT)
 *   Use this to prevent local cache from overwriting server-processed data.
 *   Example: ['content'] prevents stale local content from overwriting Gemini-processed content.
 */
export const createSyncUpdate = (tableName, options = {}) => {
  const { onForeignKeyError, onConflict = 'id', excludeFieldsOnUpdate = [] } = options;

  return async (input) => {
    // Sanitize array fields - Legend-State observables may serialize as objects {0: val, 1: val}
    // instead of proper arrays [val, val], causing Postgres error 22P02 "expected JSON array"
    const sanitizeArray = (val) =>
      val === undefined ? undefined : Array.isArray(val) ? [...val] : Array.from(val || []);

    // Ensure updated_at is always set (critical for incremental sync to detect changes)
    // This is especially important for delete operations where Legend-State
    // sets deleted=true but might not update the timestamp
    let dataWithTimestamp = {
      ...input,
      updated_at: input.updated_at || new Date().toISOString(),
      // Ensure Postgres array fields are plain JS arrays (not observable proxies)
      ...(input.hidden_block_indices !== undefined && { hidden_block_indices: sanitizeArray(input.hidden_block_indices) }),
      ...(input.tags !== undefined && { tags: sanitizeArray(input.tags) }),
    };

    // Save full payload (with all fields) before stripping — used for UPSERT fallback.
    // When the UPSERT fallback fires (row doesn't exist yet), it is effectively a CREATE
    // and must include excluded fields like 'content', otherwise the row is created without them.
    const upsertPayload = { ...dataWithTimestamp };

    // Remove excluded fields from UPDATE payload to prevent local cache from overwriting server data
    // This is critical for fields like 'content' that are processed server-side (e.g., Gemini cleanup)
    // The frontend can still INSERT with these fields (for snippets, markdown), but cannot UPDATE them
    if (excludeFieldsOnUpdate.length > 0) {
      for (const field of excludeFieldsOnUpdate) {
        if (field in dataWithTimestamp) {
          delete dataWithTimestamp[field];
        }
      }
    }

    // Strip excluded fields from a DB response row before returning to Legend-State.
    // Prevents DB null values (e.g. content=null) from overwriting a valid local cache
    // when any routine UPDATE (e.g. last_viewed_at) returns the full row.
    const stripExcluded = (row) => {
      if (!row || excludeFieldsOnUpdate.length === 0) return row;
      const stripped = { ...row };
      for (const field of excludeFieldsOnUpdate) delete stripped[field];
      return stripped;
    };

    const { data, error } = await supabase
      .from(tableName)
      .update(dataWithTimestamp)
      .eq('id', input.id)
      .select();

    // 0 rows updated - record doesn't exist, fall back to upsert.
    // Use upsertPayload (full data with all fields) so the newly created row has content.
    if (error?.code === 'PGRST116' || (!error && (!data || data.length === 0))) {
      console.warn(`Update found 0 rows for ${tableName} ${dataWithTimestamp.id}, falling back to upsert on ${onConflict}`);
      const { data: upsertData, error: upsertError } = await supabase
        .from(tableName)
        .upsert(upsertPayload, { onConflict })
        .select()
        .single();

      if (upsertError) {
        if (upsertError.code === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
          const fixedData = onForeignKeyError(upsertPayload);
          const { data: retryData, error: retryError } = await supabase
            .from(tableName)
            .upsert(fixedData, { onConflict })
            .select()
            .single();
          if (retryError) throw new Error(retryError.message);
          return stripExcluded(retryData);
        }
        throw new Error(upsertError.message);
      }
      return stripExcluded(upsertData);
    }

    if (error) {
      const errorCode = error.code;
      console.error(`${tableName} update error:`, error);

      // 23503 - Foreign key constraint violation
      if (errorCode === SYNC_ERROR_TYPES.FOREIGN_KEY && onForeignKeyError) {
        console.warn(`Foreign key error for ${tableName}, auto-fixing...`);
        const fixedData = onForeignKeyError(dataWithTimestamp);
        const { data: retryData, error: retryError } = await supabase
          .from(tableName)
          .update(fixedData)
          .eq('id', dataWithTimestamp.id)
          .select();
        if (retryError) throw new Error(retryError.message);
        return stripExcluded(retryData?.[0]) || null;
      }

      throw new Error(error.message);
    }

    return stripExcluded(data?.[0]) || null;
  };
};

/**
 * Force-full-sync detection.
 *
 * After forceUpdate() or forceCleanAndRelogin(), a localStorage flag is set.
 * On the next page load, we detect this flag and skip incremental sync
 * (changesSince: 'last-sync') so that ALL data is fetched from scratch.
 *
 * This ensures a clean full sync after any cleanup operation — if lastSync
 * metadata survives, the flag ensures we still get a full sync instead of
 * a partial incremental one.
 */
export const FORCE_FULL_SYNC_KEY = 'reedle-force-full-sync';
export const shouldForceFullSync = (() => {
  try {
    if (localStorage.getItem(FORCE_FULL_SYNC_KEY) === 'true') {
      localStorage.removeItem(FORCE_FULL_SYNC_KEY);
      // console.log('[syncHelpers] Force-full-sync flag detected — skipping incremental sync this session');
      return true;
    }
  } catch {
    // localStorage may be unavailable in some contexts
  }
  return false;
})();

/**
 * Default syncedSupabase configuration for stores
 *
 * IMPORTANT: These fields work together for proper local-first sync:
 * - fieldDeleted: Enables soft delete (set deleted=true instead of removing)
 * - fieldCreatedAt/fieldUpdatedAt: Track record timestamps for incremental sync
 * - changesSince: Only fetch changes since last sync (requires fieldUpdatedAt)
 *
 * Without all of these, deletes may not sync properly and ghost records can appear.
 */
export const defaultSyncConfig = {
  realtime: true,
  // Soft delete configuration
  fieldDeleted: 'deleted',
  // Timestamp fields for incremental sync
  fieldCreatedAt: 'created_at',
  fieldUpdatedAt: 'updated_at',
  // Enable incremental sync - only fetch changes since last sync
  // When force-full-sync flag is set, disable incremental to fetch everything
  changesSince: shouldForceFullSync ? undefined : 'last-sync',
  // Delay remote sync until auth is ready. Does NOT block local persistence
  // loading — localStorage cached data appears immediately (offline-first).
  // Prevents empty-state flash after clean-and-relogin: without this, sync fires
  // before auth → 0 rows → user logs in → no re-sync → empty articles list.
  waitFor: isAuthed$,
  retry: {
    times: 3,
    backoff: 'exponential',
    maxDelay: 30000,
  },
};

